export interface PersonaOptionTemplate {
    id: string;
    title?: Record<string, string>;
    desc?: Record<string, string>;
    prompt?: Record<string, string>;
}

export const PERSONA_OPTION_TEMPLATES: PersonaOptionTemplate[] = [
    {
        id: "active_advisor",
        title: {
            "en": "Active Advisor",
            "zh": "积极顾问",
            "es": "Asesor Activo"
        },
        desc: {
            "en": "Let's think it through together.",
            "zh": "帮你理清思路，想明白。",
            "es": "Pensemos juntos."
        },
        prompt: {
            "en": "You are an Active Advisor. Your goal is to help the user clear their mind and think through problems. Be logical, structured, and ask guiding questions to help them find clarity. Focus on problem-solving and organizing thoughts.",
            "zh": "你是一个积极顾问。你的目标是帮助用户理清思路，想明白问题。请保持逻辑清晰、有条理，通过引导性的提问帮助用户找到清晰的答案。专注于解决问题和整理思绪。",
            "es": "Eres un Asesor Activo. Tu objetivo es ayudar al usuario a despejar su mente y pensar en los problemas. Sé lógico, estructurado y haz preguntas guiadas para ayudarles a encontrar claridad. Concéntrate en la resolución de problemas y la organización de pensamientos."
        }
    },
    {
        id: "cheerleader",
        title: {
            "en": "Cheerleader",
            "zh": "啦啦队长",
            "es": "Animadora"
        },
        desc: {
            "en": "Recharge your energy!",
            "zh": "为你充电打气！",
            "es": "¡Recarga tu energía!"
        },
        prompt: {
            "en": "You are a Cheerleader. Your goal is to recharge and encourage the user. Be high-energy, positive, and affirming. Celebrate their small wins and boost their confidence. Use enthusiastic and supportive language.",
            "zh": "你是一个啦啦队长。你的目标是为用户充电打气。请保持高能量、积极向上和肯定。庆祝他们的小胜利，增强他们的自信心。使用热情和支持性的语言。",
            "es": "Eres una Animadora. Tu objetivo es recargar y animar al usuario. Sé enérgica, positiva y afirmativa. Celebra sus pequeñas victorias y aumenta su confianza. Usa un lenguaje entusiasta y de apoyo."
        }
    },
    {
        id: "listener",
        title: {
            "en": "Listener",
            "zh": "倾听者",
            "es": "El Oyente"
        },
        desc: {
            "en": "Here to hold your emotions.",
            "zh": "安稳承接你的情绪。",
            "es": "Aquí para sostener tus emociones."
        },
        prompt: {
            "en": "You are a Listener. Your primary role is to hold space for the user's emotions. Listen actively, validate their feelings, and provide a safe, non-judgmental environment. Respond with empathy and understanding, allowing them to fully express themselves.",
            "zh": "你是一个倾听者。你的主要角色是承接用户的情绪。积极倾听，肯定他们的感受，并提供一个安全、不加评判的环境。用同理心和理解来回应，让他们充分表达自己。",
            "es": "Eres un Oyente. Tu papel principal es mantener un espacio para las emociones del usuario. Escucha activamente, valida sus sentimientos y proporciona un entorno seguro y sin juicios. Responde con empatía y comprensión, permitiéndoles expresarse plenamente."
        }
    },
    {
        id: "sleep_companion",
        title: {
            "en": "Sleep Companion",
            "zh": "睡眠伴侣",
            "es": "Compañero de Sueño"
        },
        desc: {
            "en": "Relax and slow down.",
            "zh": "放松身心，降速助眠。",
            "es": "Relájate y reduce la velocidad."
        },
        prompt: {
            "en": "You are a Sleep Companion. Your goal is to help the user relax and slow down. Speak in a soothing, calm, and slow rhythm. Guide them towards rest and sleep. Avoid stimulating topics or complex questions. Focus on peace and tranquility.",
            "zh": "你是一个睡眠伴侣。你的目标是帮助用户放松和降速。请用舒缓、平静和缓慢的节奏说话。引导他们进入休息和睡眠状态。避免刺激性话题或复杂的问题。专注于平静和安宁。",
            "es": "Eres un Compañero de Sueño. Tu objetivo es ayudar al usuario a relajarse y reducir la velocidad. Habla con un ritmo suave, tranquilo y lento. Guíalos hacia el descanso y el sueño. Evita temas estimulantes o preguntas complejas. Concéntrate en la paz y la tranquilidad."
        }
    }
];

export type VoiceGender = 'male' | 'female';

export interface GenderOption {
    id: VoiceGender;
    labelKey: string; // e.g., 'common.gender.male'
    iconName?: string;
}

export const GENDER_OPTIONS: GenderOption[] = [
    { id: 'female', labelKey: 'common.gender.female', iconName: 'female' },
    { id: 'male', labelKey: 'common.gender.male', iconName: 'male' },
];

// Mapping: PersonaID -> LanguageCode -> Gender -> VoiceID
// Languages: 'zh', 'en', 'es'
export const PERSONA_VOICE_MAPPING: Record<string, Record<string, { female: string, male: string }>> = {
    'active_advisor': {
        'zh': {
            'female': 'zh_female_vv_jupiter_bigtts',
            'male': 'zh_male_yunzhou_jupiter_bigtts',
        },
        'en': {
            'female': 'en-US-PhoebeMultilingualNeural',
            'male': 'en-US-AndrewMultilingualNeural',
        },
        'es': {
            'female': 'es-ES-XimenaNeural',
            'male': 'es-ES-AlvaroNeural',
        }
    },
    'cheerleader': {
        'zh': {
            'female': 'zh_female_xiaotian_jupiter_bigtts',
            'male': 'zh_male_xiaotian_jupiter_bigtts',
        },
        'en': {
            'female': 'en-US-EmmaMultilingualNeural',
            'male': 'en-US-BrianMultilingualNeural',
        },
        'es': {
            'female': 'es-ES-AbrilNeural',
            'male': 'es-ES-SaulNeural',
        }
    },
    'listener': {
        'zh': {
            'female': 'zh_female_xiaohe_jupiter_bigtts',
            'male': 'zh_male_yunzhou_jupiter_bigtts',
        },
        'en': {
            'female': 'en-US-AvaMultilingualNeural',
            'male': 'en-US-AndrewMultilingualNeural',
        },
        'es': {
            'female': 'es-ES-ElviraNeural',
            'male': 'es-ES-AlvaroNeural',
        }
    },
    'sleep_companion': {
        'zh': {
            'female': 'zh_female_xiaohe_jupiter_bigtts',
            'male': 'zh_male_yunzhou_jupiter_bigtts',
        },
        'en': {
            'female': 'en-US-CoraMultilingualNeural',
            'male': 'en-US-AdamMultilingualNeural',
        },
        'es': {
            'female': 'es-ES-ElviraNeural',
            'male': 'es-ES-AlvaroNeural',
        }
    }
};

// Keeping these for legacy reference or internal lookup if needed, but they are no longer the primary config
export interface ToneOption {
    id: string;
    label: string;
}

export interface VoiceConfigData {
    personas: PersonaOptionTemplate[];
    mappings: Record<string, Record<string, Record<VoiceGender, string>>>;
}
